import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMyPlanSummary, useInvalidateUsageQueries } from '../../hooks/useUsage';
import { useInvoices } from '../../hooks/useInvoices';
import { usePurchaseSessionVerification } from '../../hooks/usePurchaseSessionVerification';
import { getPlanDisplayName } from '../../config/plans';
import { Button, getButtonClasses, BUTTON_ICON_GLYPH_CLASS } from '@/components/ui/atoms/Button';
import { StatusPill, getPlanStatusVariant } from '../../components/ui/atoms/StatusPill';
import { Skeleton } from '../../components/ui/atoms/Skeleton';
import { ProgressBar } from '../../components/ui/atoms/ProgressBar';
import type { ProgressBarIntent } from '../../components/ui/atoms/ProgressBar';
import { EmptyState } from '../../components/ui/molecules/IllustratedEmptyState';
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  XCircle,
} from 'lucide-react';
import { formatDate } from '../../utils/date';
import { BillingPlanSelector } from './BillingPlanSelector';

interface MeterProps {
  label: string;
  used: number;
  limit: number | null;
}

const Meter = ({ label, used, limit }: MeterProps) => {
  const { t } = useTranslation('settings');
  const isUnlimited = limit === -1 || limit === null;
  const ratio = isUnlimited ? 0 : Math.min(1, used / Math.max(1, limit ?? 1));
  const nearLimit = ratio >= 0.85;
  const atLimit = ratio >= 1;
  const intent: ProgressBarIntent = atLimit ? 'error' : nearLimit ? 'secondary' : 'primary';

  return (
    <div className="rounded-lg bg-background-gray p-3 flex flex-col">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <span className="text-sm font-semibold text-dark-invirtus font-heading truncate min-w-0">
          {label}
        </span>
        <span
          className={`text-sm font-semibold tabular-nums font-body shrink-0 ${
            atLimit ? 'text-red' : 'text-dark-invirtus'
          }`}
        >
          {used}
          <span className="text-dark-invirtus/40 font-medium"> / {isUnlimited ? '∞' : limit}</span>
        </span>
      </div>
      <ProgressBar
        value={ratio * 100}
        max={100}
        size="sm"
        intent={intent}
        aria-label={label}
      />
      <div className="mt-1.5 text-xs text-dark-invirtus/60 font-body">
        {isUnlimited
          ? t('billing.unlimited')
          : atLimit
            ? t('billing.limit_reached')
            : t('billing.remaining', { count: (limit ?? 0) - used })}
      </div>
    </div>
  );
};

const formatInvoiceAmount = (amountMinor: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
};

const INVOICE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral'> = {
  paid: 'success',
  open: 'warning',
  draft: 'neutral',
  uncollectible: 'warning',
  void: 'neutral',
};

/** Banner shown when returning from a Stripe/Berexia checkout redirect. */
const CheckoutResultBanner = ({
  result,
  sessionId,
  onDismiss,
}: {
  result: 'success' | 'cancelled';
  sessionId: string | null;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation('settings');
  const invalidateUsage = useInvalidateUsageQueries();
  // Only verify when we returned with a success + session id (Stripe).
  const verifySessionId = result === 'success' ? sessionId : null;
  const { state, isLoading, error } = usePurchaseSessionVerification(verifySessionId);

  // Refresh plan data once verification succeeds.
  useEffect(() => {
    if (state === 'success') {
      invalidateUsage();
    }
  }, [state, invalidateUsage]);

  if (result === 'cancelled') {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-light-lila/60 bg-light p-4">
        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-dark-invirtus/60" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-dark-invirtus font-heading">
            {t('billing.payment_cancelled_title')}
          </p>
          <p className="mt-0.5 text-sm text-dark-invirtus/60 font-body">
            {t('billing.payment_cancelled_desc')}
          </p>
        </div>
        <Button
          type="text"
          intent="muted"
          size="xs"
          label={t('billing.dismiss')}
          onClick={onDismiss}
        />
      </div>
    );
  }

  // result === 'success'
  if (result === 'success' && !sessionId) {
    // Berexia returns without a session id — treat as activated, plan refresh will reflect it.
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-light-lila/60 bg-purple-soft p-4">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-dark-invirtus" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-dark-invirtus font-heading">
            {t('billing.payment_received_title')}
          </p>
          <p className="mt-0.5 text-sm text-dark-invirtus/70 font-body">
            {t('billing.payment_received_desc')}
          </p>
        </div>
        <Button
          type="text"
          intent="muted"
          size="xs"
          label={t('billing.dismiss')}
          onClick={onDismiss}
        />
      </div>
    );
  }

  if (isLoading || state === 'pending') {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-light-lila/60 bg-light p-4">
        <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-dark-invirtus/60" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-dark-invirtus font-heading">
            {t('billing.confirming_title')}
          </p>
          <p className="mt-0.5 text-sm text-dark-invirtus/60 font-body">
            {t('billing.confirming_desc')}
          </p>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-light-lila/60 bg-purple-soft p-4">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-dark-invirtus" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-dark-invirtus font-heading">
            {t('billing.plan_activated_title')}
          </p>
          <p className="mt-0.5 text-sm text-dark-invirtus/70 font-body">
            {t('billing.plan_activated_desc')}
          </p>
        </div>
        <Button
          type="text"
          intent="muted"
          size="xs"
          label={t('billing.dismiss')}
          onClick={onDismiss}
        />
      </div>
    );
  }

  // failure / unknown
  return (
    <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-soft bg-red-soft/40 p-4">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red" />
      <div className="flex-1">
          <p className="text-sm font-semibold text-dark-invirtus font-heading">
            {t('billing.payment_error_title')}
          </p>
          <p className="mt-0.5 text-sm text-dark-invirtus/60 font-body">
            {error || t('billing.payment_error_fallback')}
          </p>
        </div>
        <Button
          type="text"
          intent="muted"
          size="xs"
          label={t('billing.dismiss')}
          onClick={onDismiss}
        />
    </div>
  );
};

/** Invoices card — real Stripe invoices via /api/billing/invoices. */
const InvoicesCard = () => {
  const { t } = useTranslation('settings');
  const { data: invoices, isLoading, error } = useInvoices();

  return (
    <div className="lg:col-span-5 rounded-xl border border-light-lila/40 bg-light p-5 flex flex-col">
      <div className="mb-4">
        <h6 className="text-sm font-semibold text-dark-invirtus font-heading">{t('billing.invoices_title')}</h6>
        <p className="mt-0.5 text-xs text-dark-invirtus/60 font-body">
          {t('billing.invoices_desc')}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-start gap-2 rounded-lg bg-red-soft/40 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red" />
          <p className="text-xs text-dark-invirtus font-body">
            {t('billing.invoices_load_error')} {error.message}
          </p>
        </div>
      ) : !invoices || invoices.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-dark-invirtus-soft mb-3">
            <FileText className="h-5 w-5 text-dark-invirtus/60" />
          </div>
          <h6 className="text-sm font-semibold text-dark-invirtus font-heading">
            {t('billing.no_invoices_title')}
          </h6>
          <p className="mt-1 text-xs text-dark-invirtus/60 font-body max-w-xs">
            {t('billing.no_invoices_desc')}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-light-lila/40">
          {invoices.map((inv) => (
            <li
              key={inv.id}
              className="flex items-center gap-4 py-3 first:pt-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-dark-invirtus font-heading truncate">
                    {inv.number || inv.id}
                  </span>
                  {inv.status && (
                    <StatusPill
                      variant={INVOICE_STATUS_VARIANT[inv.status] ?? 'neutral'}
                    >
                      {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                    </StatusPill>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-dark-invirtus/60 font-body">
                  {formatDate(inv.created)}
                </div>
              </div>
              <div className="shrink-0 text-sm font-semibold text-dark-invirtus font-heading tabular-nums">
                {formatInvoiceAmount(inv.amount_paid, inv.currency)}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {inv.hosted_invoice_url && (
                  <a
                    href={inv.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={getButtonClasses('icon', 'muted', 'sm', false)}
                    aria-label={t('billing.view_invoice')}
                    title={t('billing.view_invoice')}
                  >
                    <ExternalLink className={BUTTON_ICON_GLYPH_CLASS.sm} />
                  </a>
                )}
                {inv.invoice_pdf && (
                  <a
                    href={inv.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={getButtonClasses('icon', 'muted', 'sm', false)}
                    aria-label={t('billing.download_pdf')}
                    title={t('billing.download_pdf')}
                  >
                    <Download className={BUTTON_ICON_GLYPH_CLASS.sm} />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const BillingPage = () => {
  const { t } = useTranslation('settings');
  const { data: planSummary, isLoading, error } = useMyPlanSummary();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep link: ?action=choose-plan opens the in-page plan selector directly
  // (used by the Manage / Upgrade CTAs elsewhere in the app).
  const [view, setView] = useState<'summary' | 'choose-plan'>(
    searchParams.get('action') === 'choose-plan' ? 'choose-plan' : 'summary',
  );

  // Read checkout return params (set by Stripe/Berexia success/cancel URLs).
  const checkout = searchParams.get('checkout');
  const checkoutSessionId = searchParams.get('session_id');
  const [checkoutResult, setCheckoutResult] = useState<'success' | 'cancelled' | null>(
    checkout === 'success' || checkout === 'cancelled' ? checkout : null,
  );

  // Clear the query params once consumed so a refresh doesn't re-trigger.
  useEffect(() => {
    if (
      checkout === 'success' ||
      checkout === 'cancelled' ||
      searchParams.get('action')
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      next.delete('session_id');
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planLabel = useMemo(
    () => getPlanDisplayName(planSummary?.fixed_plan_code || planSummary?.plan_kind),
    [planSummary],
  );

  return (
    <div className="bg-background-gray h-full overflow-y-auto">
      <div className="px-6 lg:px-10 py-6 md:py-10">
        <div className="mb-8 max-w-3xl">
          <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight">
            {t('billing.title')}
          </h4>
          <p className="mt-1 text-sm text-dark-invirtus/60 font-body">
            {t('billing.description')}
          </p>
        </div>

        {checkoutResult && (
          <CheckoutResultBanner
            result={checkoutResult}
            sessionId={checkoutSessionId}
            onDismiss={() => setCheckoutResult(null)}
          />
        )}

        {view === 'choose-plan' ? (
          <BillingPlanSelector
            onBack={() => setView('summary')}
            initialPack={planSummary?.fixed_plan_code || undefined}
            onActivated={() => setView('summary')}
          />
        ) : isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-soft bg-red-soft/40 p-5 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red shrink-0 mt-0.5" />
            <div className="text-sm text-dark-invirtus font-body">{error.message}</div>
          </div>
        ) : !planSummary || !planSummary.has_plan ? (
          <div className="rounded-xl border border-light-lila/40 bg-light p-8">
            <EmptyState
              icon={<CreditCard />}
              title={t('billing.no_plan_title')}
              description={t('billing.no_plan_desc')}
            />
            <div className="mt-6 flex justify-center">
              <Button
                label={t('billing.choose_plan')}
                type="contained"
                intent="primary"
                onClick={() => setView('choose-plan')}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 xl:gap-8">
            {/* Left card — Plan + Usage combined */}
            <div className="lg:col-span-7 rounded-xl border border-light-lila/40 bg-light overflow-hidden flex flex-col">
              {/* Plan section */}
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-dark-invirtus/50 font-body">
                      {t('billing.current_plan')}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <h5 className="text-lg font-semibold text-dark-invirtus font-heading">
                        {planLabel}
                      </h5>
                      <StatusPill variant={getPlanStatusVariant(planSummary.expired)}>
                        {planSummary.expired ? t('billing.expired') : t('billing.active')}
                      </StatusPill>
                    </div>
                    {planSummary.expires_at && (
                      <div className="mt-1 text-sm text-dark-invirtus/60 font-body">
                        {planSummary.expired
                          ? t('billing.expired_on', { date: formatDate(planSummary.expires_at) })
                          : t('billing.renews_on', { date: formatDate(planSummary.expires_at) })}
                      </div>
                    )}
                  </div>
                  <Button
                    label={planSummary.expired ? t('billing.renew') : t('billing.change_plan')}
                    type="contained"
                    intent="secondary"
                    onClick={() => setView('choose-plan')}
                  />
                </div>
              </div>

              <div className="h-px bg-light-lila/40" />

              {/* Usage section */}
              <div className="p-5 flex-1">
                <h6 className="text-[11px] font-semibold uppercase tracking-wider text-dark-invirtus/50 font-body mb-3">
                  {t('billing.usage_this_period')}
                </h6>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Meter
                    label={t('billing.jobs_consumed')}
                    used={planSummary.usage?.job ?? 0}
                    limit={(planSummary.limits?.job ?? null) as number | null}
                  />
                  <Meter
                    label={t('billing.resumes_consumed')}
                    used={planSummary.usage?.cv ?? 0}
                    limit={(planSummary.limits?.cv ?? null) as number | null}
                  />
                  <Meter
                    label={t('billing.interviews_consumed')}
                    used={planSummary.usage?.interview ?? 0}
                    limit={(planSummary.limits?.interview ?? null) as number | null}
                  />
                  <Meter
                    label={t('billing.talent_searches_consumed')}
                    used={planSummary.usage?.talent_search ?? 0}
                    limit={(planSummary.limits?.talent_search ?? null) as number | null}
                  />
                  <Meter
                    label={t('billing.talent_unlocks_consumed')}
                    used={planSummary.usage?.talent_unlock ?? 0}
                    limit={(planSummary.limits?.talent_unlock ?? null) as number | null}
                  />
                </div>
              </div>
            </div>

            {/* Right card — Invoices */}
            <InvoicesCard />
          </div>
        )}
      </div>
    </div>
  );
};
